import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import "./ActivityCard.scss";

import assignmentImage from "@/images/assignments/default-assignment.svg";
import trashIcon from "@/images/icons/delete-trash-icon.svg";
import plusActivityIcon from "@/images/icons/activity-card-plus-icon.svg";
import checkmarkActivityIcon from "@/images/icons/checkmark-activity-icon.svg";
import StopwatchIcon from "@/images/icons/stopwatch.svg";
import newActivityIcon from "@/images/icons/new-activity-icon.svg";
import newActivitySparkleIcon from "@/images/icons/new-activity-sparkle.svg";
import { ResourceType } from "@/enums/ResourceType";
import warningIcon from "@/images/assignments/warning.svg";
import TimeToGrowthDurationEstimate from "@/components/shared/TimeToGrowthDurationEstimate";

export interface ActivityCardProps {
  activityId: string;
  title: string;
  subTitle?: string;
  iconSrc?: string;
  addToPlan?: () => void;
  unassignFromPlan?: () => void;
  isAdded?: boolean;
  isRemoving?: boolean;
  isNew?: boolean;
  timeAssigned?: number;
  url?: string;
  sharedSkills?: string[];
  isResourceAssigned?: boolean;
}

enum ActivityCardResourceTypeDisplay {
  microLesson = "Microlesson",
  skillTutor = "Targeted Amira Tutoring",
  tutor = "Amira Tutoring",
  teacherResource = "Teacher Resource",
  istationLegacyActivity = "Digital Instruction by Istation",
  textSet = "Text Set",
  ERSS = "Skill Scaffold",
  EPS = "Amira Tutoring",
}

const getTooltipText = (sharedSkills: string[], isAdded: boolean) => {
  if (!sharedSkills || sharedSkills.length === 0) {
    return "";
  }
  let tooltipText = ` time assigned for ${sharedSkills.length - 1} other skill${sharedSkills.length > 2 ? "s" : ""}`;

  if (isAdded) {
    tooltipText = `Adding this resource will also add it to the` + tooltipText + ".";
  } else {
    tooltipText = `Removing this resource will also remove it from the` + tooltipText + ".";
  }

  return tooltipText;
};

const ActivityCard: React.FC<ActivityCardProps> = ({
  activityId,
  title,
  subTitle,
  iconSrc = assignmentImage,
  addToPlan,
  unassignFromPlan,
  isAdded = false,
  timeAssigned,
  isNew,
  url,
  sharedSkills,
  isResourceAssigned = true
}) => {
  const { t } = useTranslation();

  const handleTitleClick = (e: React.MouseEvent) => {
    if (subTitle === ResourceType.teacherResource && url) {
      e.preventDefault();
      window.open(url, '_blank');
    }
  };

  return (
    <section
      className="d-flex flex-nowrap flex-column align-items-center w-100 aec-activity-card"
      data-testid={`aec-activity-card-${activityId}`}
    >
      <div className="aec-activity-card-icon-container d-flex flex-nowrap align-items-center w-100">
        <img
          data-testid={`aec-activity-card-icon-${activityId}`}
          className="aec-activity-card-icon"
          src={iconSrc}
          role="presentation"
        />
        <div
          className={
            addToPlan || unassignFromPlan
              ? "aec-activity-card-shorter-text"
              : "aec-activity-card-text"
          }
        >
          {subTitle && <span className="aec-activity-card-sub-title">{ActivityCardResourceTypeDisplay[subTitle as keyof typeof ActivityCardResourceTypeDisplay]}</span>}
          {title && (
            <span
              className={`aec-activity-card-title ${subTitle === ResourceType.teacherResource ? 'clickable' : ''}`}
              onClick={handleTitleClick}
              title={title}
            >
              {title}
            </span>
          )}
          {isNew && <div className="aec-activity-card-new-tag">
            <img src={newActivityIcon} alt="new" />
          </div>}
          {isNew && <div className="aec-activity-card-new-tag-sparkle">
            <img src={newActivitySparkleIcon} alt="new sparkle" />
          </div>}
        </div>

        {(addToPlan || unassignFromPlan) && (
          <div className="aec-activity-card-controls d-flex align-items-center justify-content-center">
            {timeAssigned !== undefined && timeAssigned >= 0 && (
              <div className="me-2">
                <TimeToGrowthDurationEstimate
                  duration={timeAssigned}
                  timeAssignedIcon={StopwatchIcon}
                  variant="resource-card"
                />
              </div>
            )}
            {addToPlan && (
              <button
                onClick={addToPlan}
                className="activity-card-add-button"
              >
                <div className="d-flex flex-nowrap align-items-center">
                  <img src={plusActivityIcon} role="presentation" className="aec-activity-card-add-icon" />
                </div>
              </button>
            )}
            {unassignFromPlan && (
              <button
                onClick={unassignFromPlan}
                className="aec-activity-card-unassign"
              >
                <span className="d-flex flex-nowrap align-items-center">
                  <img src={trashIcon} role="presentation" className="aec-activity-card-unassign-icon" />
                </span>
              </button>
            )}
          </div>
        )}
        {!(addToPlan || unassignFromPlan) && timeAssigned && (
          <TimeToGrowthDurationEstimate
            duration={timeAssigned}
            timeAssignedIcon={StopwatchIcon}
            variant="resource-card"
          />
        )}
      </div>
      {sharedSkills && sharedSkills.length > 0 && (
        <div className="aec-activity-card-shared-skills d-flex flex-nowrap align-items-center w-100 gap-2">
          <img src={warningIcon} alt="warning" className="warning-icon" />
          <span>{getTooltipText(sharedSkills, isNew || !isResourceAssigned)}</span>
        </div>
      )}
    </section>
  );
};

export default ActivityCard;
